import React, { FC, useState } from 'react';
import { createPortal } from 'react-dom';
import { useForm } from 'react-hook-form';
import isEqual from 'lodash/isEqual';
import { useRouter } from 'next/compat/router';
import { useModal } from '../Modal/useModal';
import { WriteInTerritoryPicker } from './WriteInTerritoryPicker';
import { getNewArticlePageUrl } from '../../services/pagesUrl/getNewArticlePageUrl';
import { PromptTypePicker } from '../Prompt/constants';
import { CloseConfirmationModal } from '../ConfirmationModal/CloseConfirmationModal';
import { TerritoryVersionType } from '../../layouts/TerritoryPageLayout/TerritoryPageLayout';
import { getFeatureFlag } from '../FeatureFlags/getFeatureFlag';
import { getPromptCreatePageUrl } from '../../services/pagesUrl/getPromptCreatePageUrl';

type TWriteInTerritoryContainerProps = {
  territoryId: string;
  territorySlug: string;
  territoryVersion: TerritoryVersionType;
  territoryPageRef: React.RefObject<HTMLDivElement>;
};

export const WriteInTerritoryContainer: FC<TWriteInTerritoryContainerProps> = ({
  territoryId,
  territorySlug,
  territoryPageRef,
}) => {
  const router = useRouter();

  const [isPromptCardSelected, setIsPromptCardSelected] = useState(false);
  const [promptType, setPromptType] = useState<PromptTypePicker>();

  const onCloseNewInTerritoryModalCallback = () => {
    setPromptType(PromptTypePicker.STANDARD);
    setIsPromptCardSelected(false);
    onClosePromptModal();
  };

  const {
    openModal: openPromptConfirmationModal,
    closeModal: closePromptConfirmationModal,
    ModalComponent: PromptConfirmationModalComponent,
  } = useModal();

  const {
    ModalComponent: WriteInTerritoryModal,
    openModal: openWriteInTerritoryModal,
    closeModal: closeWriteInTerritoryModal,
  } = useModal({
    closeModalCallback: onCloseNewInTerritoryModalCallback,
  });

  const newTerritoryPostUrl = getNewArticlePageUrl({ territoryId });
  const defaultValues = {
    title: '',
    description: '',
    startDate: new Date(),
    endDate: new Date(),
  };

  const useFormReturn = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues,
  });

  const onClosePromptModal = () => {
    const {
      getValues,
      formState: { defaultValues, isSubmitting },
    } = useFormReturn;

    if (!isSubmitting && !isEqual(getValues(), defaultValues)) {
      openPromptConfirmationModal();
    }
  };

  const onPromptSelect = () => {
    if (getFeatureFlag('promptV2')) {
      closeWriteInTerritoryModal();
      router?.push(getPromptCreatePageUrl(territorySlug));
    } else {
      setIsPromptCardSelected(true);
      setPromptType(PromptTypePicker.STANDARD);
    }
  };

  const onBackClick = () => {
    setIsPromptCardSelected(false);
    setPromptType(PromptTypePicker.STANDARD);
  };

  if (!territoryPageRef?.current) {
    return null;
  }

  return (
    <>
      {createPortal(
        <>
          <WriteInTerritoryModal
            title={isPromptCardSelected ? 'Create a new prompt' : 'Create New'}
            subtitle={
              isPromptCardSelected
                ? 'Spark a discussion with Territory members.'
                : 'Write a new post or prompt to share with your Territory members.'
            }
            containerClassName="sm:!max-h-[90%] !max-w-[640px] pt-16 sm:pt-6"
          >
            <WriteInTerritoryPicker
              closeModal={closeWriteInTerritoryModal}
              territoryId={territoryId}
              useFormReturn={useFormReturn}
              url={newTerritoryPostUrl}
              isPromptCardSelected={isPromptCardSelected}
              onPromptSelect={onPromptSelect}
              onBackClick={onBackClick}
              promptType={promptType}
              setPromptType={setPromptType}
              territorySlug={territorySlug}
            />
          </WriteInTerritoryModal>
          <CloseConfirmationModal
            ModalComponent={PromptConfirmationModalComponent}
            closeConfirmationModal={closePromptConfirmationModal}
            closeModal={closeWriteInTerritoryModal}
            openModal={openWriteInTerritoryModal}
          />
        </>,
        territoryPageRef.current,
      )}
    </>
  );
};
